import { ButtonBase, Box } from "@mui/material";

// One grant, one control. A pressed pill is a permission the role holds; an
// unpressed one is a permission it could hold. The dashed outline on the
// unpressed state says "available but not taken" in a way an empty checkbox
// cannot -- an empty checkbox in a grid of empty checkboxes reads as
// "nothing configured here yet".
//
// `capability` switches the palette to amber. Capabilities are feature-specific
// authority (force resolve, approve), not CRUD, and they are the grants worth a
// second look during an audit.
const PALETTE = {
    standard: {
        on: { bg: "#EFEEFD", border: "#C7C3F4", text: "#3A32A8", dot: "#4D44D8" },
        hover: { border: "#4D44D8", text: "#3A32A8" },
    },
    capability: {
        on: { bg: "#FEF6E0", border: "#EDC584", text: "#8A4B08", dot: "#B45309" },
        hover: { border: "#EDC584", text: "#8A4B08" },
    },
};

const PermissionPill = ({
    label,
    checked,
    capability,
    featureName,
    // Legend swatches: the same visual, with none of the button semantics. A
    // legend entry announced as a pressed toggle would tell a screen reader the
    // role holds a permission called "Granted".
    decorative,
    onToggle,
    testId,
}) => {
    const tone = PALETTE[capability ? "capability" : "standard"];

    const visual = {
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.5,
        px: 1.4,
        py: 0.5,
        borderRadius: 999,
        color: checked ? tone.on.text : "#8A8CA6",
        backgroundColor: checked ? tone.on.bg : "transparent",
        border: checked ? `1px solid ${tone.on.border}` : "1px dashed #D1D3E3",
    };

    const dot = (
        <Box
            component="span"
            sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                flex: "none",
                backgroundColor: checked ? tone.on.dot : "#D1D3E3",
            }}
        />
    );

    if (decorative) {
        return (
            <Box component="span" sx={visual}>
                {dot}
                {label}
            </Box>
        );
    }

    return (
        <ButtonBase
            type="button"
            aria-pressed={!!checked}
            // The visible label is a verb with no subject. In a list of 22 rows
            // of identical verbs, a screen reader needs the row to make sense.
            aria-label={featureName ? `${label} on ${featureName}` : label}
            onClick={onToggle}
            data-testid={testId}
            disableRipple
            sx={{
                ...visual,
                m: "3px 6px 3px 0",
                transition: "all .12s",
                "&:hover": {
                    borderColor: tone.hover.border,
                    borderStyle: "solid",
                    color: tone.hover.text,
                },
                "&:focus-visible": { outline: "2px solid #4D44D8", outlineOffset: 1 },
            }}
        >
            {dot}
            {label}
        </ButtonBase>
    );
};

export default PermissionPill;
